<template>
    <div v-if="visible" id="verification-modal" class="modal" :style="{ display: 'block' }" @click.self="onClose">
        <div class="modal-content-container">
            <div class="modal-content">
                <div class="verification-content-container">
                    <!-- Title -->
                    <div class="modal-header">
                        <h3 class="modal-title">{{ t('verifyThisDevice') }}</h3>
                    </div>

                    <!-- OTP Input -->
                    <div class="modal-body">
                        <form @submit.prevent="verifyDevice">
                            <div class="form-group mb-3 pt-2">
                                <label for="otpInput" class="form-label">{{ t('enterOtp') }}</label>

                                <div class="warning-message" v-if="errorMsg">
                                    <p>{{ errorMsg }}</p>
                                </div>

                                <PinInput @update:otp="onOtpEntry" />
                            </div>

                            <!-- Action Button -->
                            <div class="modal-actions-container text-center">
                                <button :disabled="btnDisabled" type="submit"
                                    :class="['btn', btnDisabled ? 'btn-charcoal' : 'btn-blue']"
                                    :aria-label="t('verifyThisDevice')" @click="handleVerifyAndConnect">
                                    <span v-if="loading" class="spinner" :aria-label="t('verifyThisDevice')"></span>
                                    {{ t('verify') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { useDeviceVerifierStore } from '@/stores/deviceVerifier';
import { useSystemSettingsStore } from '@/stores/systemSettings';
import { usePos } from "@/composables/usePos";
import PinInput from '@/components/order/form/pinInput.vue';

const props = defineProps({
    visible: Boolean,
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const { handlePosAssociation } = usePos();
const { getErrorMessage, setErrorMessage, setIsVerified, setUuid, closePosVerifierModal} = useDeviceVerifierStore();
const { setPosClientId } = useSystemSettingsStore();

const errorMsg = computed(() => getErrorMessage()) ;

const loading = ref(false);
const btnDisabled = ref(true);
const otp = ref('');

// Close modal
const onClose = () => {
    emit('close');
};

const onOtpEntry = (code) => {
    otp.value = code;
};

const handleVerifyAndConnect = async () => {
    loading.value = true;
    let errorMsg = ''
    setErrorMessage(errorMsg);
    try {
        const uuid = uuidv4().replace(/-/g, '').slice(0, 16);
        const response = await handlePosAssociation(0, "verifyOtp", {
            "uuid": uuid,
            "otp": String(otp.value)
        });
        if (response?.status) {
            /* Update store data regarding device verification */
            setIsVerified(true);
            setUuid(uuid);
            closePosVerifierModal();
            /**
             * Set client id in system settings in localStorage.
             * NOTE: 
             * Specially the client id, as it is used in other parts of the code. 
             * if not handled then event handling will fail.
             */
            setPosClientId(uuid)
            /**
             * This is used to fetch custom settings done by the user.
             * This is used in the pos logo.
             * If not set, then the pos logo will not be updated.
             */
            localStorage.setItem('fetchCustomSystemSettings', true);
            /* Relaod the page to fetch data */
            window.location.href = "/";
        } else {
            errorMsg = response?.detail || 'Failed to verify OTP';
            setErrorMessage(errorMsg);
            console.warn(errorMsg);
        }
    } catch (error) {
        errorMsg = error?.detail || 'Error verifying OTP';
        setErrorMessage(errorMsg);
        console.error(errorMsg);
    } finally {
        loading.value = false;
    }
};

const removeErrorMessage = () => {
    setTimeout(() => {
      setErrorMessage("");
    }, 3000);
};

onMounted(() => {
    removeErrorMessage();
});

watch(() => props.visible, (newVal) => {
    if (newVal) {
        otp.value = '';
    }
});

watch(otp, (newVal) => {
    btnDisabled.value = (newVal.length === 6) ? false : true;
});

watch(errorMsg, (newVal) => {
  if (newVal) {
    removeErrorMessage();
  }
});
</script>

<style scoped>
#verification-modal {
    z-index: 999999;
}

.modal-header, .modal-body {
    text-align: center;
}

.modal-actions-container {
    justify-content: center;
    align-items: center;
}

.spinner {
    display: inline-block;
    width: 1em;
    height: 1em;
    border: 2px solid #fff;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    margin-right: 0.5em;
    vertical-align: middle;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.warning-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 12px 16px;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    font-weight: 500;
    margin: 10px 0;
}
</style>